import React, { Fragment } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { MdErrorOutline as NoChapterContentIcon } from "react-icons/md";
import { MdCloudOff as NoInternetIcon } from "react-icons/md";
import scrollIntoView from "scroll-into-view-if-needed";
import { I18n } from "@aws-amplify/core";

import LoadingSpinner from "../../UI/LoadingSpinner";
import ScriptureView from "../../ScriptureView/ScriptureView";
import Media from "../../../utils/media";
import { isCordovaApp, isOnline } from "../../../utils/cordovaUtils";
import { withBible } from "../../Bible/BibleContainer";
import { withChapter } from "../ChapterContainer";
import { withAudio } from "../ChapterAudio/ChapterAudio";

const StyledEmptyContent = styled.div`
  display: flex;
  align-items: center;
  min-height: 300px;
  margin: auto;
  svg {
    margin: auto;
  }
`;

const ChapterCol = styled.div`
  position: relative;
  flex: 1;
  padding: 4px 12px;
  padding-bottom: ${props =>
    props.touchEnabled
      ? "80px"
      : "20px"}; // for showing copyright info on initial scroll to bottom

  ${Media.desktopEmbedSafe`
    padding: 12px 12px 70px 12px;
    overflow-y: initial;
  `};
`;

const StyledChapterView = styled.div`
  display: flex;
  flex-direction: ${props =>
    props.theme.scriptDirection === "rtl" ? "row-reverse" : "row"};
  height: ${props => {
    if (!props.touchEnabled) return "auto";
    return props.hasAudio
      ? `calc(100vh - ${47 + 84}px)`
      : `calc(100vh - ${47}px)`;
  }};
  // overflow-y: scroll;
  ${props =>
    !props.touchEnabled && props.hasAudio ? "margin-bottom: 72px;" : ""}
  overflow-y: ${props => (!props.touchEnabled ? "initial" : "scroll")};
  -webkit-overflow-scrolling: touch;
  ${Media.desktopEmbedSafe`
    height: auto;
    margin-bottom: unset;
    overflow-y: initial;
    `};
`;

const StyledBibleVersion = styled.div`
  margin-top: 10px;
  font-family: Noto Serif;
  font-style: normal;
  font-weight: bold;
  line-height: normal;
  font-size: 0.7rem;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  height: 17px;
  color: ${props => (props.theme.darkMode ? "white" : "black")};
`;

const StyledCurrentChapter = styled.div`
  margin-top: 10px;
  font-family: Noto Serif;
  font-style: normal;
  font-weight: bold;
  line-height: normal;
  font-size: 1.125rem;
  text-align: center;
  height: 25px;
  color: ${props => (props.theme.darkMode ? "white" : "black")};
`;

const Copyright = styled.div`
  text-align: center;
  font-family: ${props => props.theme.uiFont};
  font-size: 0.7em;
  padding: 10px 20px 2px;
  ${Media.desktop`
    padding: 30px 40px 2px;
  `};
  a {
    color: #666;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const PrivacyAndTOC = styled.div`
  display: block;
  margin-top: 8px;
  ${Media.desktop`
  display: none;
  margin-top: 0px;
  `};
`;
class ChapterView extends React.Component {
  static propTypes = {
    chapterId: PropTypes.string,
    chapter: PropTypes.shape({
      getCurrentChapter: PropTypes.func.isRequired,
      getChapter: PropTypes.func.isRequired,
      onRefClick: PropTypes.func.isRequired,
      onVerseSelect: PropTypes.func.isRequired,
      selectedVerseOrgIds: PropTypes.arrayOf(PropTypes.string),
      highlightedVerseOrgIds: PropTypes.arrayOf(PropTypes.string),
      matchedVerseOrgIds: PropTypes.arrayOf(PropTypes.string),
      onVerseHover: PropTypes.func.isRequired,
      onVerseUnhover: PropTypes.func.isRequired,
      isLoading: PropTypes.func.isRequired,
      passageId: PropTypes.string
    }).isRequired,
    audio: PropTypes.shape({
      isControllerOpen: PropTypes.bool.isRequired,
      isPlayable: PropTypes.bool.isRequired,
      getCurrentAudioChapter: PropTypes.func.isRequired
    }).isRequired,
    bible: PropTypes.shape({
      biblesById: PropTypes.shape({}).isRequired,
      parallelBibleIds: PropTypes.arrayOf(PropTypes.string).isRequired,
      currentBibleId: PropTypes.string.isRequired
    }).isRequired,
    touchEnabled: PropTypes.bool
  };

  static defaultProps = {
    touchEnabled: false,
    chapterId: null,
    highlightedVerseOrgIds: []
  };

  componentDidMount() {
    const {
      chapter: { passageId }
    } = this.props;
    const passageStartId = passageId ? passageId.split("-")[0] : "";
    const passageElements = document.querySelectorAll(
      `.verse-span[data-org-ids="${passageStartId}"]`
    );

    const firstPassageElement = passageElements[0];
    if (!firstPassageElement) return;
    scrollIntoView(firstPassageElement, {
      scrollMode: "if-needed",
      block: "center"
    });
  }

  shouldComponentUpdate = nextProps =>
    nextProps.chapter.getCurrentChapter() &&
    nextProps.chapter.getCurrentChapter().content !== null;

  renderParallels({ chapter }) {
    const { parallelBibleIds, biblesById } = this.props.bible;

    return parallelBibleIds.map((parallelId, index) => {
      const parallel =
        chapter && chapter.parallels
          ? chapter.parallels.find(
              parallelObj => parallelObj.bibleId === parallelId
            )
          : {};

      if (parallel && parallel.content && biblesById[parallel.bibleId]) {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <ChapterCol
            key={`${parallelId}-${index}`}
            touchEnabled={this.props.touchEnabled}
          >
            {chapter && !chapter.id.includes("intro") && (
              <Fragment>
                <StyledBibleVersion>
                  {biblesById[parallelId].nameLocal}
                </StyledBibleVersion>
                <StyledCurrentChapter>
                  {parallel.reference}
                </StyledCurrentChapter>
              </Fragment>
            )}
            <ScriptureView
              bibleId={parallel.bibleId}
              scriptDirection={
                biblesById[parallel.bibleId].language.scriptDirection
              }
              content={parallel.content}
              highlightSelectedVerse
              onVerseHover={this.props.chapter.onVerseHover}
              onVerseUnhover={this.props.chapter.onVerseUnhover}
              highlightedVerseOrgIds={this.props.chapter.highlightedVerseOrgIds}
              matchedVerseOrgIds={this.props.chapter.matchedVerseOrgIds}
              onVerseSelect={this.props.chapter.onVerseSelect}
              onRefClick={this.props.chapter.onRefClick}
              selectedVerseOrgIds={this.props.chapter.selectedVerseOrgIds}
              showVerseActions
            />
            <Copyright>
              <Link to={`/bible/${parallel.bibleId}/info`}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: parallel.copyright
                  }}
                />
              </Link>
            </Copyright>
          </ChapterCol>
        );
      }

      return (
        <ChapterCol
          key={`${parallelId}-${index}`}
          touchEnabled={this.props.touchEnabled}
        >
          <StyledEmptyContent>
            {(chapter && chapter.isNullChapter) ||
            (chapter &&
              !chapter.isNullChapter &&
              !parallel &&
              chapter.parallelBibleIds.join(",") ===
                parallelBibleIds.join(",") &&
              !this.props.chapter.isLoading()) ? (
              <NoChapterContentIcon style={{ fontSize: "6rem" }} />
            ) : isCordovaApp && !isOnline() ? (
              <NoInternetIcon size={50} />
            ) : (
              <LoadingSpinner size={50} />
            )}
          </StyledEmptyContent>
        </ChapterCol>
      );
    });
  }

  render() {
    const {
      audio,
      chapter,
      chapterId,
      isCurrentChapter,
      touchEnabled
    } = this.props;
    const { biblesById, currentBibleId, getCurrentBible } = this.props.bible;

    const currentBible = getCurrentBible();
    const currentChapter = chapterId
      ? chapter.getChapter({
          bibleId: currentBibleId,
          chapterId
        })
      : chapter.getCurrentChapter();

    const currentChapterRef = currentChapter
      ? currentChapter.reference.replace(" ", "-")
      : "";
    const currentAudioChapter = audio.getCurrentAudioChapter();

    return (
      <StyledChapterView
        id={isCurrentChapter ? "current-chapter" : ""}
        touchEnabled={touchEnabled}
        hasAudio={audio.isControllerOpen && audio.isPlayable}
      >
        {currentChapter &&
          !currentChapter.isNullChapter &&
          biblesById[currentChapter.bibleId] && (
            <ChapterCol
              id={isCurrentChapter ? "current-chapter-col" : ""}
              key="primary"
              touchEnabled={touchEnabled}
            >
              {currentChapter && !currentChapter.id.includes("intro") && (
                <Fragment>
                  <StyledBibleVersion>
                    {biblesById[currentBibleId].nameLocal}
                  </StyledBibleVersion>
                  <StyledCurrentChapter>
                    {currentChapter.reference}
                  </StyledCurrentChapter>
                </Fragment>
              )}
              <ScriptureView
                bibleId={currentBibleId}
                currentChapterRef={currentChapterRef}
                scriptDirection={
                  currentBible ? currentBible.language.scriptDirection : "LTR"
                }
                content={currentChapter ? currentChapter.content : null}
                highlightSelectedVerse
                onVerseHover={chapter.onVerseHover}
                onVerseUnhover={chapter.onVerseUnhover}
                highlightedVerseOrgIds={chapter.highlightedVerseOrgIds}
                matchedVerseOrgIds={chapter.matchedVerseOrgIds}
                onVerseSelect={chapter.onVerseSelect}
                onRefClick={chapter.onRefClick}
                selectedVerseOrgIds={chapter.selectedVerseOrgIds}
                showVerseActions
              />
              <Copyright>
                <Link to={`/bible/${currentBibleId}/info`}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: currentChapter.copyright
                    }}
                  />
                  {currentAudioChapter && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: currentAudioChapter.copyright
                      }}
                    />
                  )}
                </Link>
                <PrivacyAndTOC>
                  <Link to="/privacy">{I18n.get("Privacy")}</Link> |{" "}
                  <Link to="/terms">{I18n.get("Terms and Conditions")}</Link>
                </PrivacyAndTOC>
              </Copyright>
            </ChapterCol>
          )}
        {(!currentChapter || currentChapter.isNullChapter) && (
          <ChapterCol key="primary" touchEnabled={touchEnabled}>
            {currentChapter && currentChapter.isNullChapter ? (
              <StyledEmptyContent>
                <NoChapterContentIcon style={{ fontSize: "6rem" }} />
              </StyledEmptyContent>
            ) : (
              <StyledEmptyContent>
                <LoadingSpinner size={50} />
              </StyledEmptyContent>
            )}
          </ChapterCol>
        )}

        {this.renderParallels({ chapter: currentChapter })}
      </StyledChapterView>
    );
  }
}
export default withAudio(withChapter(withBible(ChapterView)));
